/**
 * Sign-out that survives being offline (V-R6). The session is an HttpOnly cookie only the API can revoke; a
 * sign-out that cannot reach it leaves a pending marker, and the viewer query (`useMe`) revokes the session first
 * and treats the device as signed out until that has happened, so reconnecting never signs the last user back in.
 * A later sign-in clears the marker (`resetSession` with a user).
 */
import { request } from './client'
import { isApiError } from './errors'

export const PENDING_SIGNOUT_KEY = 'algoboo.signout.pending'

function unreachable(e: unknown): boolean {
  return isApiError(e) ? e.status === 0 || e.status >= 500 : true
}

export function signOutPending(): boolean {
  try {
    return localStorage.getItem(PENDING_SIGNOUT_KEY) !== null
  } catch {
    return false
  }
}

export function clearPendingSignOut(): void {
  try {
    localStorage.removeItem(PENDING_SIGNOUT_KEY)
  } catch {
    // storage blocked
  }
}

/** Revoke the session; when the API is unreachable, remember to do it before the viewer is read again. */
export async function endSession(): Promise<void> {
  try {
    await request('logout', {})
  } catch (e) {
    if (!unreachable(e)) return // 401: already signed out server-side
    try {
      localStorage.setItem(PENDING_SIGNOUT_KEY, new Date().toISOString())
    } catch {
      // storage blocked: the in-memory reset still applies to this page
    }
  }
}

/** Finish a pending sign-out. True when no session from before it can remain (nothing pending, or revoked now). */
export async function flushPendingSignOut(): Promise<boolean> {
  if (!signOutPending()) return true
  try {
    await request('logout', {})
  } catch (e) {
    if (unreachable(e)) return false
  }
  clearPendingSignOut()
  return true
}
